import { useTranslation } from 'react-i18next';

import { NgNotificationBadge, NgTab, NgTabPanel } from '@/ui';
import type { IncidentListRow } from '@ngsos/portal-core/incidents';
import type { Incident, PortalCall } from '@/signalr/contracts';
import type { IncidentListContext } from './incidentListContext';
import { CallNotificationList } from './CallNotificationList';
import { IncidentListPageList } from './IncidentListPageList';

export type IncidentListTabProps = {
  isLoading?: boolean;
  forwardedIncidents: readonly IncidentListRow[];
  currentlyOpenIncidents: readonly IncidentListRow[];
  recentClosedIncidents: readonly IncidentListRow[];
  incidents: readonly Incident[];
  callNotifications: readonly PortalCall[];
  onTabSwitch?: (context: IncidentListContext) => void;
};

export function IncidentListTab({
  isLoading = true,
  forwardedIncidents,
  currentlyOpenIncidents,
  recentClosedIncidents,
  incidents,
  callNotifications,
  onTabSwitch,
}: IncidentListTabProps) {
  const { t } = useTranslation();
  const notificationCount = (count: number) => (isLoading ? '-' : String(count));

  const changeContext = (tabId: string) => {
    if (tabId === 'incidents') {
      onTabSwitch?.('incidents');
    } else if (tabId === 'call-notifications') {
      onTabSwitch?.('callNotifications');
    }
  };

  return (
    <NgTabPanel onTabChanged={changeContext}>
      <NgTab
        id="incidents"
        header={
          <>
            <span>{t('IncidentList_TabTitle')}</span>
            <NgNotificationBadge
              text={notificationCount(forwardedIncidents.length)}
              color="blue"
              additionalClasses="notification-count-badge"
            />
            <NgNotificationBadge
              text={notificationCount(currentlyOpenIncidents.length)}
              color="orange"
              additionalClasses="notification-count-badge"
            />
          </>
        }
        content={
          <IncidentListPageList
            forwardedIncidents={forwardedIncidents}
            currentlyOpenIncidents={currentlyOpenIncidents}
            recentClosedIncidents={recentClosedIncidents}
            isLoading={isLoading}
          />
        }
      />
      <NgTab
        id="call-notifications"
        testID="incoming-calls-tab"
        header={
          <>
            <span>{t('CallNotification_IncomingCalls')}</span>
            <NgNotificationBadge
              text={notificationCount(callNotifications.length)}
              color="black"
              additionalClasses="notification-count-badge"
            />
          </>
        }
        content={
          <CallNotificationList callNotifications={callNotifications} incidents={incidents} isLoading={isLoading} />
        }
      />
    </NgTabPanel>
  );
}
